import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  Crown, 
  TrendingUp, 
  Users, 
  DollarSign,
  Settings,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  Plus,
  AlertTriangle,
  X
} from "lucide-react";

// Firebase imports - plus d'axios !
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionPlanCard = ({ plan, onEdit, onToggle, onDelete }) => {
  const getBadgeColor = (planName) => {
    if (planName.includes('Gratuit')) return 'bg-gray-500';
    if (planName.includes('Standard')) return 'bg-blue-500';
    if (planName.includes('Premium')) return 'bg-purple-500';
    return 'bg-orange-500';
  };

  const getCommissionText = (rate) => {
    if (rate === 0) return 'Aucune commission';
    return `${(rate * 100).toFixed(0)}% de commission`;
  };

  return (
    <Card className={`${plan.is_active ? '' : 'opacity-50'} hover:shadow-lg transition-shadow`}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="flex items-center">
              <Crown className="w-5 h-5 mr-2 text-orange-600" />
              {plan.name}
            </CardTitle>
            <CardDescription className="mt-2">
              {getCommissionText(plan.commission_rate)}
            </CardDescription>
          </div>
          <Badge className={getBadgeColor(plan.name)}>
            {plan.is_active ? 'Actif' : 'Inactif'}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Prix mensuel</p>
              <p className="text-2xl font-bold text-orange-600">
                {plan.price_monthly === 0 ? 'Gratuit' : `${plan.price_monthly}€`}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Prix annuel</p>
              <p className="text-2xl font-bold text-orange-600">
                {plan.price_yearly === 0 ? 'Gratuit' : `${plan.price_yearly}€`}
              </p>
              {plan.price_yearly > 0 && plan.price_monthly > 0 && (
                <p className="text-xs text-green-600">
                  Économie: {((plan.price_monthly * 12 - plan.price_yearly) / (plan.price_monthly * 12) * 100).toFixed(0)}%
                </p>
              )}
            </div>
          </div>
          
          <div>
            <p className="text-sm font-medium mb-2">Fonctionnalités :</p>
            <ul className="text-sm space-y-1">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          
          <div className="flex flex-col space-y-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onEdit(plan)}
              className="w-full"
            >
              <Edit className="w-4 h-4 mr-1" />
              Modifier
            </Button>
            <Button 
              variant={plan.is_active ? "destructive" : "default"}
              size="sm"
              onClick={() => onToggle(plan)}
              className="w-full"
            >
              {plan.is_active ? (
                <>
                  <XCircle className="w-4 h-4 mr-1" />
                  Désactiver
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Activer
                </>
              )}
            </Button>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => onDelete && onDelete(plan.id)}
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Supprimer
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RevenueStats = ({ revenueData }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Chiffre d'Affaires Total</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{revenueData?.total_revenue?.toFixed(2) || 0}€</div>
          <p className="text-xs text-muted-foreground">
            Sur {revenueData?.total_bookings || 0} réservations
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Commissions Plateforme</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-orange-600">
            {revenueData?.platform_commission?.toFixed(2) || 0}€
          </div>
          <p className="text-xs text-muted-foreground">
            {revenueData?.total_revenue > 0 ? 
              ((revenueData.platform_commission / revenueData.total_revenue) * 100).toFixed(1) : 0}% du CA
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Paiements Chefs</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">
            {revenueData?.chef_payouts?.toFixed(2) || 0}€
          </div>
          <p className="text-xs text-muted-foreground">
            Revenus versés aux chefs
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Abonnements</CardTitle>
          <Crown className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-purple-600">
            {revenueData?.subscription_revenue?.toFixed(2) || 0}€
          </div>
          <p className="text-xs text-muted-foreground">
            Revenus récurrents
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const ChefSubscriptionsList = ({ subscriptions }) => {
  const getPlanBadgeColor = (planName) => {
    if (planName.includes('Gratuit')) return 'bg-gray-500';
    if (planName.includes('Standard')) return 'bg-blue-500';
    if (planName.includes('Premium')) return 'bg-purple-500';
    return 'bg-orange-500';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Abonnements des Chefs</CardTitle>
        <CardDescription>
          Gestion des abonnements actifs et historique
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {subscriptions.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              Aucun abonnement payant pour le moment
            </p>
          ) : (
            subscriptions.map((subscription) => (
              <div key={subscription.id} className="flex justify-between items-center p-4 border rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium">{subscription.chef_email}</h4>
                  <p className="text-sm text-gray-600">
                    Plan: {subscription.plan_name} • 
                    Commission: {(subscription.commission_rate * 100).toFixed(0)}% • 
                    {subscription.price_paid}€/{subscription.billing_period === 'yearly' ? 'an' : 'mois'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Du {new Date(subscription.start_date).toLocaleDateString('fr-FR')} au{' '}
                    {new Date(subscription.end_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className={getPlanBadgeColor(subscription.plan_name)}>
                    {subscription.plan_name}
                  </Badge>
                  <Badge 
                    variant={subscription.status === 'active' ? 'default' : 'secondary'}
                    className={subscription.status === 'active' ? 'bg-green-600' : ''}
                  >
                    {subscription.status === 'active' ? 'Actif' : subscription.status}
                  </Badge>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const SubscriptionManagement = ({ user }) => {
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [revenueData, setRevenueData] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null
  });
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalData, setAlertModalData] = useState({
    title: '',
    message: '',
    type: 'success'
  });

  // États pour les paramètres de la plateforme
  const [platformSettings, setPlatformSettings] = useState({
    defaultCommission: 20,
    minPayout: 50,
    currency: 'EUR',
    taxRate: 20,
    platformFee: 2.5,
    autoPayouts: true,
    emailNotifications: true,
    smsNotifications: false,
    analyticsEnabled: true
  });

  // Fonction pour sauvegarder les paramètres
  const handleSavePlatformSettings = async () => {
    try {
      // Sauvegarder les paramètres dans Firestore
      const settingsRef = doc(db, 'platform-settings', 'default');
      const settingsData = {
        ...platformSettings,
        updated_at: new Date(),
        updated_by: 'contact@kila-france.com'
      };
      
      await setDoc(settingsRef, settingsData);
      
      console.log('💾 Paramètres sauvegardés dans Firestore:', settingsData);
      showAlert('Succès', 'Paramètres de la plateforme sauvegardés avec succès !', 'success');
    } catch (error) {
      console.error('Erreur sauvegarde paramètres:', error);
      showAlert('Erreur', error.message || 'Erreur lors de la sauvegarde des paramètres', 'error');
    }
  };

  // États pour la création/modification de plans
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price_monthly: '',
    price_yearly: '',
    commission_rate: '',
    features: [],
    is_active: true,
    max_chefs: '',
    analytics: true,
    priority_support: false,
    api_access: false
  });

  // Helper functions for modals
  const showConfirm = (title, message, onConfirm) => {
    setConfirmModalData({
      title,
      message,
      onConfirm,
      onCancel: () => setShowConfirmModal(false)
    });
    setShowConfirmModal(true);
  };

  const showAlert = (title, message, type = 'success') => {
    setAlertModalData({ title, message, type });
    setShowAlertModal(true);
    setTimeout(() => setShowAlertModal(false), 4000);
  };

  // Mode démo - données d'abonnements Firebase CORRIGÉES
  const getDemoPlans = () => [
    {
      id: 'plan-gratuit',
      name: 'Plan Gratuit',
      description: 'Parfait pour débuter dans la cuisine',
      price_monthly: 0,
      price_yearly: 0,
      commission_rate: 0.20,
      features: [
        'Accès aux ateliers de base',
        'Support communautaire',
        'Commission 20%'
      ],
      is_active: true,
      color: 'bg-gray-500',
      max_chefs: 5,
      analytics: true,
      priority_support: false
    },
    {
      id: 'plan-standard',
      name: 'Plan Standard',
      description: 'Pour les passionnés de cuisine',
      price_monthly: 49,
      price_yearly: 490, // 2 mois gratuits (49*10)
      commission_rate: 0.10,
      features: [
        'Accès à tous les ateliers',
        'Support prioritaire',
        'Commission 10%',
        'Réservation prioritaire',
        'Jusqu\'à 50 chefs'
      ],
      is_active: true,
      color: 'bg-orange-500',
      max_chefs: 50,
      analytics: true,
      priority_support: true
    },
    {
      id: 'plan-premium',
      name: 'Plan Premium',
      description: 'Pour les professionnels',
      price_monthly: 99,
      price_yearly: 990, // 2 mois gratuits (99*10)
      commission_rate: 0.0,
      features: [
        'Accès illimité',
        'Support VIP 24/7',
        'Commission 0%',
        'Ateliers exclusifs',
        'Certification',
        'Chefs illimités',
        'API personnalisée'
      ],
      is_active: true,
      color: 'bg-purple-500',
      max_chefs: -1, // illimité
      analytics: true,
      priority_support: true,
      api_access: true
    }
  ];

  const getDemoSubscriptions = () => [
    {
      id: 'sub-1',
      chef_name: 'Chef Marie Dubois',
      plan_name: 'Plan Premium',
      monthly_fee: 99,
      commission_rate: 0.0,
      status: 'active',
      start_date: '2024-01-15',
      revenue: 1250
    },
    {
      id: 'sub-2',
      chef_name: 'Chef Antoine Moreau',
      plan_name: 'Plan Standard',
      monthly_fee: 49,
      commission_rate: 0.10,
      status: 'active',
      start_date: '2024-02-01',
      revenue: 890
    }
  ];

  const getDemoRevenueData = () => ({
    total_revenue: 3117,
    monthly_revenue: 148,
    active_subscriptions: 2,
    commission_earned: 89,
    growth_rate: 15.3
  });

  // Charger les données depuis Firebase Firestore
  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      
      console.log('📊 Chargement données abonnements depuis Firebase');
      
      // Charger les plans d'abonnement depuis Firestore
      const plansSnapshot = await getDocs(collection(db, 'subscription-plans'));
      const plansData = plansSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Charger les abonnements actifs (données de démo pour l'instant)
      const subscriptionsData = getDemoSubscriptions();

      // Données de revenus (démo pour l'instant)
      const revenueData = getDemoRevenueData();

      if (plansData.length > 0) {
        setPlans(plansData);
        setSubscriptions(subscriptionsData);
        setRevenueData(revenueData);

        console.log(`✅ ${plansData.length} plans d'abonnement chargés depuis Firestore`);
        console.log(`✅ ${subscriptionsData.length} abonnements actifs chargés (démo)`);
      } else {
        console.log('⚠️ Aucun plan dans Firestore, utilisation des données de démo');
        return loadDemoData();
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement depuis Firestore:', error);
      console.log('⚠️ Erreur Firestore, fallback vers mode démo');
      return loadDemoData();
    } finally {
      setLoading(false);
    }
  };

  // Fonction fallback pour les données de démonstration
  const loadDemoData = async () => {
    // Simuler un délai réseau
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const demoPlans = getDemoPlans();
    const demoSubscriptions = getDemoSubscriptions();
    const demoRevenue = getDemoRevenueData();
    
    setPlans(demoPlans);
    setSubscriptions(demoSubscriptions);
    setRevenueData(demoRevenue);
    
    console.log(`✅ ${demoPlans.length} plans d'abonnement chargés (démo)`);
    console.log(`✅ ${demoSubscriptions.length} abonnements actifs chargés (démo)`);
  };

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-green-500';
      case 'inactive': return 'bg-red-500';
      case 'pending': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getPlanColor = (planName) => {
    if (planName.includes('Premium')) return 'bg-purple-500';
    if (planName.includes('Standard')) return 'bg-orange-500';
    return 'bg-gray-500';
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name || '',
      description: plan.description || '',
      price_monthly: plan.price_monthly ? plan.price_monthly.toString() : '0',
      price_yearly: plan.price_yearly ? plan.price_yearly.toString() : '0',
      commission_rate: plan.commission_rate ? (plan.commission_rate * 100).toString() : '15',
      features: plan.features ? [...plan.features] : [],
      is_active: plan.is_active !== undefined ? plan.is_active : true,
      max_chefs: plan.max_chefs === -1 ? 'unlimited' : (plan.max_chefs ? plan.max_chefs.toString() : '10'),
      analytics: plan.analytics !== undefined ? plan.analytics : true,
      priority_support: plan.priority_support !== undefined ? plan.priority_support : false,
      api_access: plan.api_access !== undefined ? plan.api_access : false
    });
    setShowEditPlanModal(true);
  };

  const handleCreatePlan = () => {
    setPlanForm({
      name: '',
      description: '',
      price_monthly: '',
      price_yearly: '',
      commission_rate: '15',
      features: [],
      is_active: true,
      max_chefs: '10',
      analytics: true,
      priority_support: false,
      api_access: false
    });
    setShowCreatePlanModal(true);
  };

  const handleSavePlan = async (isEditing = false) => {
    try {
      // Validation des données
      if (!planForm.name || !planForm.description) {
        showAlert('Erreur', 'Le nom et la description sont obligatoires', 'error');
        return;
      }

      const planData = {
        name: planForm.name,
        description: planForm.description,
        price_monthly: parseFloat(planForm.price_monthly) || 0,
        price_yearly: parseFloat(planForm.price_yearly) || 0,
        commission_rate: parseFloat(planForm.commission_rate) / 100,
        features: planForm.features,
        is_active: planForm.is_active,
        max_chefs: planForm.max_chefs === 'unlimited' ? -1 : parseInt(planForm.max_chefs) || 10,
        analytics: planForm.analytics,
        priority_support: planForm.priority_support,
        api_access: planForm.api_access,
        updated_at: new Date()
      };

      if (isEditing) {
        // Modification du plan existant dans Firestore
        const planRef = doc(db, 'subscription-plans', editingPlan.id);
        await updateDoc(planRef, planData);
        
        // Mettre à jour le plan existant dans la liste locale
        const updatedPlan = { ...editingPlan, ...planData };
        const updatedPlans = plans.map(p => 
          p.id === editingPlan.id ? updatedPlan : p
        );
        setPlans(updatedPlans);
        
        showAlert('Succès', `Plan "${planData.name}" modifié avec succès !`, 'success');
        console.log('✅ Plan modifié dans Firestore:', editingPlan.id);
        setShowEditPlanModal(false);
        setEditingPlan(null);
      } else {
        // Création d'un nouveau plan dans Firestore
        const newPlanId = `plan-${Date.now()}`;
        const newPlanData = {
          ...planData,
          id: newPlanId,
          created_at: new Date()
        };
        
        const planRef = doc(db, 'subscription-plans', newPlanId);
        await setDoc(planRef, newPlanData);
        
        // Ajouter le nouveau plan à la liste locale
        setPlans(prevPlans => [...prevPlans, newPlanData]);
        
        showAlert('Succès', `Plan "${planData.name}" créé avec succès !`, 'success');
        console.log('✅ Nouveau plan créé dans Firestore:', newPlanId);
        setShowCreatePlanModal(false);
      }

      // Reset form
      setPlanForm({
        name: '',
        description: '',
        price_monthly: '',
        price_yearly: '',
        commission_rate: '15',
        features: [],
        is_active: true,
        max_chefs: '10',
        analytics: true,
        priority_support: false,
        api_access: false
      });

      console.log('💾 Plan sauvegardé dans Firebase Firestore');
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showAlert('Erreur', error.message || 'Erreur lors de la sauvegarde du plan', 'error');
    }
  };

  const handleDeletePlan = async (planId) => {
    const planToDelete = plans.find(p => p.id === planId);
    
    showConfirm(
      'Supprimer le plan',
      `Êtes-vous sûr de vouloir supprimer le plan "${planToDelete.name}" ?\n\nCette action est irréversible et peut affecter les abonnements existants.`,
      async () => {
        try {
          // Supprimer le plan de Firestore
          const planRef = doc(db, 'subscription-plans', planId);
          await deleteDoc(planRef);
          
          // Mettre à jour la liste locale
          const updatedPlans = plans.filter(p => p.id !== planId);
          setPlans(updatedPlans);
          
          showAlert('Succès', `Plan "${planToDelete.name}" supprimé avec succès !`, 'success');
          console.log('✅ Plan supprimé de Firestore:', planId);
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          showAlert('Erreur', error.message || 'Erreur lors de la suppression du plan', 'error');
        }
        setShowConfirmModal(false);
      }
    );
  };

  const handleTogglePlan = async (plan) => {
    const action = plan.is_active ? 'désactiver' : 'activer';
    
    const confirmToggle = () => {
      setShowConfirmModal(false);
      executeTogglePlan(plan, action);
    };

    showConfirm(
      `Confirmer ${action} le plan`,
      `Êtes-vous sûr de vouloir ${action} le plan "${plan.name}" ?

Cette action peut affecter :
• Les abonnements existants
• La visibilité du plan pour les nouveaux clients
• Les fonctionnalités disponibles

L'action sera effective immédiatement en mode démo.`,
      confirmToggle
    );
  };

  const executeTogglePlan = async (plan, action) => {
    try {
      const newStatus = !plan.is_active;
      
      // Mettre à jour le statut du plan dans Firestore
      const planRef = doc(db, 'subscription-plans', plan.id);
      await updateDoc(planRef, { 
        is_active: newStatus,
        updated_at: new Date()
      });
      
      // Mettre à jour le plan dans la liste locale
      const updatedPlans = plans.map(p => 
        p.id === plan.id ? { ...p, is_active: newStatus } : p
      );
      setPlans(updatedPlans);
      
      const statusMessage = newStatus ? 'activé' : 'désactivé';
      showAlert('Action réussie', `Plan "${plan.name}" ${statusMessage} avec succès !`, 'success');
      console.log(`✅ Plan ${statusMessage} dans Firestore:`, plan.id);
      
    } catch (error) {
      console.error("Error toggling plan:", error);
      showAlert('Erreur', error.message || `Erreur lors de la ${action} du plan. Veuillez réessayer.`, 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p>Chargement des données d'abonnement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Gestion des Abonnements</h2>
        <p className="text-gray-600">Configurez les plans et suivez les revenus de la plateforme</p>
      </div>

      <RevenueStats revenueData={revenueData} />

      <Tabs defaultValue="plans" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="plans">Plans d'Abonnement</TabsTrigger>
          <TabsTrigger value="subscriptions">Abonnements Actifs</TabsTrigger>
          <TabsTrigger value="settings">Paramètres</TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="mt-6">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold">Plans Disponibles</h3>
              <Button 
                className="bg-orange-600 hover:bg-orange-700"
                onClick={handleCreatePlan}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Plan
              </Button>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <SubscriptionPlanCard
                  key={plan.id}
                  plan={plan}
                  onEdit={handleEditPlan}
                  onToggle={handleTogglePlan}
                  onDelete={handleDeletePlan}
                />
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="subscriptions" className="mt-6">
          <ChefSubscriptionsList subscriptions={subscriptions} />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Paramètres de la Plateforme
              </CardTitle>
              <CardDescription>
                Configuration générale des commissions et paiements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Paramètres Financiers</h4>
                  
                  <div className="space-y-2">
                    <Label htmlFor="default-commission">Commission par défaut (%)</Label>
                    <Input
                      id="default-commission"
                      type="number"
                      value={platformSettings.defaultCommission}
                      onChange={(e) => setPlatformSettings({
                        ...platformSettings, 
                        defaultCommission: parseFloat(e.target.value) || 0
                      })}
                      min="0"
                      max="100"
                    />
                    <p className="text-xs text-gray-500">Commission pour les comptes gratuits</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="min-payout">Paiement minimum (€)</Label>
                    <Input
                      id="min-payout"
                      type="number"
                      value={platformSettings.minPayout}
                      onChange={(e) => setPlatformSettings({
                        ...platformSettings, 
                        minPayout: parseFloat(e.target.value) || 0
                      })}
                      min="0"
                    />
                    <p className="text-xs text-gray-500">Montant minimum pour déclencher un paiement</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="currency">Devise</Label>
                    <select
                      id="currency"
                      className="w-full p-2 border rounded-md"
                      value={platformSettings.currency}
                      onChange={(e) => setPlatformSettings({
                        ...platformSettings, 
                        currency: e.target.value
                      })}
                    >
                      <option value="EUR">EUR (€)</option>
                      <option value="USD">USD ($)</option>
                      <option value="GBP">GBP (£)</option>
                      <option value="CHF">CHF (₣)</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tax-rate">Taux de TVA (%)</Label>
                    <Input
                      id="tax-rate"
                      type="number"
                      value={platformSettings.taxRate}
                      onChange={(e) => setPlatformSettings({
                        ...platformSettings, 
                        taxRate: parseFloat(e.target.value) || 0
                      })}
                      min="0"
                      max="100"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="platform-fee">Frais de plateforme (%)</Label>
                    <Input
                      id="platform-fee"
                      type="number"
                      value={platformSettings.platformFee}
                      onChange={(e) => setPlatformSettings({
                        ...platformSettings, 
                        platformFee: parseFloat(e.target.value) || 0
                      })}
                      min="0"
                      max="10"
                      step="0.1"
                    />
                    <p className="text-xs text-gray-500">Frais prélevés sur chaque transaction</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Paramètres Système</h4>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Paiements automatiques</Label>
                        <p className="text-xs text-gray-500">Versement automatique aux chefs</p>
                      </div>
                      <Button 
                        variant={platformSettings.autoPayouts ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPlatformSettings({
                          ...platformSettings, 
                          autoPayouts: !platformSettings.autoPayouts
                        })}
                      >
                        {platformSettings.autoPayouts ? 'Activé' : 'Désactivé'}
                      </Button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Notifications email</Label>
                        <p className="text-xs text-gray-500">Notifications aux chefs et clients</p>
                      </div>
                      <Button 
                        variant={platformSettings.emailNotifications ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPlatformSettings({
                          ...platformSettings, 
                          emailNotifications: !platformSettings.emailNotifications
                        })}
                      >
                        {platformSettings.emailNotifications ? 'Activé' : 'Désactivé'}
                      </Button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Notifications SMS</Label>
                        <p className="text-xs text-gray-500">SMS pour les événements critiques</p>
                      </div>
                      <Button 
                        variant={platformSettings.smsNotifications ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPlatformSettings({
                          ...platformSettings, 
                          smsNotifications: !platformSettings.smsNotifications
                        })}
                      >
                        {platformSettings.smsNotifications ? 'Activé' : 'Désactivé'}
                      </Button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Analytics</Label>
                        <p className="text-xs text-gray-500">Collecte de données analytiques</p>
                      </div>
                      <Button 
                        variant={platformSettings.analyticsEnabled ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPlatformSettings({
                          ...platformSettings, 
                          analyticsEnabled: !platformSettings.analyticsEnabled
                        })}
                      >
                        {platformSettings.analyticsEnabled ? 'Activé' : 'Désactivé'}
                      </Button>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h5 className="font-medium text-blue-900 mb-2">Configuration Actuelle</h5>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>• Commission défaut: {platformSettings.defaultCommission}%</p>
                      <p>• Paiement minimum: {platformSettings.minPayout}€</p>
                      <p>• Devise: {platformSettings.currency}</p>
                      <p>• TVA: {platformSettings.taxRate}%</p>
                      <p>• Frais plateforme: {platformSettings.platformFee}%</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">
                      Ces paramètres affectent tous les nouveaux abonnements et transactions.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Dernière modification: {new Date().toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <Button 
                    onClick={handleSavePlatformSettings}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Sauvegarder les Paramètres
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-orange-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {confirmModalData.title}
                </h3>
              </div>
              <p className="text-gray-600 mb-6 whitespace-pre-line">
                {confirmModalData.message}
              </p>
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={confirmModalData.onCancel}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={confirmModalData.onConfirm}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Confirmer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {showAlertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  {alertModalData.type === 'success' ? (
                    <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {alertModalData.title}
                  </h3>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAlertModal(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-gray-600 mb-4 whitespace-pre-line">
                {alertModalData.message}
              </p>
              <div className="flex justify-end">
                <Button 
                  onClick={() => setShowAlertModal(false)}
                  className={alertModalData.type === 'success' 
                    ? "bg-green-600 hover:bg-green-700" 
                    : "bg-red-600 hover:bg-red-700"
                  }
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Plan Modal */}
      {showCreatePlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Créer un Nouveau Plan</h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowCreatePlanModal(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="plan-name">Nom du plan *</Label>
                    <Input
                      id="plan-name"
                      value={planForm.name}
                      onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                      placeholder="Ex: Plan Professionnel"
                    />
                  </div>
                  <div>
                    <Label htmlFor="plan-description">Description *</Label>
                    <Input
                      id="plan-description"
                      value={planForm.description}
                      onChange={(e) => setPlanForm({...planForm, description: e.target.value})}
                      placeholder="Ex: Pour les chefs expérimentés"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="price-monthly">Prix mensuel (€)</Label>
                    <Input
                      id="price-monthly"
                      type="number"
                      value={planForm.price_monthly}
                      onChange={(e) => setPlanForm({...planForm, price_monthly: e.target.value})}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="price-yearly">Prix annuel (€)</Label>
                    <Input
                      id="price-yearly"
                      type="number"
                      value={planForm.price_yearly}
                      onChange={(e) => setPlanForm({...planForm, price_yearly: e.target.value})}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="commission-rate">Commission (%)</Label>
                    <Input
                      id="commission-rate"
                      type="number"
                      value={planForm.commission_rate}
                      onChange={(e) => setPlanForm({...planForm, commission_rate: e.target.value})}
                      placeholder="15"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="max-chefs">Nombre maximum de chefs</Label>
                  <select
                    id="max-chefs"
                    className="w-full p-2 border rounded-md"
                    value={planForm.max_chefs}
                    onChange={(e) => setPlanForm({...planForm, max_chefs: e.target.value})}
                  >
                    <option value="5">5 chefs</option>
                    <option value="10">10 chefs</option>
                    <option value="25">25 chefs</option>
                    <option value="50">50 chefs</option>
                    <option value="100">100 chefs</option>
                    <option value="unlimited">Illimité</option>
                  </select>
                </div>

                <div>
                  <Label>Fonctionnalités (une par ligne)</Label>
                  <textarea
                    className="w-full p-2 border rounded-md"
                    rows="4"
                    value={planForm.features.join('\n')}
                    onChange={(e) => setPlanForm({...planForm, features: e.target.value.split('\n').filter(f => f.trim())})}
                    placeholder="Accès aux ateliers&#10;Support prioritaire&#10;Commission réduite"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="analytics"
                      checked={planForm.analytics}
                      onChange={(e) => setPlanForm({...planForm, analytics: e.target.checked})}
                    />
                    <Label htmlFor="analytics">Analytics inclus</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="priority-support"
                      checked={planForm.priority_support}
                      onChange={(e) => setPlanForm({...planForm, priority_support: e.target.checked})}
                    />
                    <Label htmlFor="priority-support">Support prioritaire</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="api-access"
                      checked={planForm.api_access}
                      onChange={(e) => setPlanForm({...planForm, api_access: e.target.checked})}
                    />
                    <Label htmlFor="api-access">Accès API</Label>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is-active"
                    checked={planForm.is_active}
                    onChange={(e) => setPlanForm({...planForm, is_active: e.target.checked})}
                  />
                  <Label htmlFor="is-active">Plan actif dès la création</Label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button 
                  variant="outline"
                  onClick={() => setShowCreatePlanModal(false)}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={() => handleSavePlan(false)}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  Créer le Plan
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Plan Modal */}
      {showEditPlanModal && editingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Modifier le Plan "{editingPlan.name}"
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setShowEditPlanModal(false);
                    setEditingPlan(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-plan-name">Nom du plan *</Label>
                    <Input
                      id="edit-plan-name"
                      value={planForm.name}
                      onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                      placeholder="Ex: Plan Professionnel"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-plan-description">Description *</Label>
                    <Input
                      id="edit-plan-description"
                      value={planForm.description}
                      onChange={(e) => setPlanForm({...planForm, description: e.target.value})}
                      placeholder="Ex: Pour les chefs expérimentés"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="edit-price-monthly">Prix mensuel (€)</Label>
                    <Input
                      id="edit-price-monthly"
                      type="number"
                      value={planForm.price_monthly}
                      onChange={(e) => setPlanForm({...planForm, price_monthly: e.target.value})}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-price-yearly">Prix annuel (€)</Label>
                    <Input
                      id="edit-price-yearly"
                      type="number"
                      value={planForm.price_yearly}
                      onChange={(e) => setPlanForm({...planForm, price_yearly: e.target.value})}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-commission-rate">Commission (%)</Label>
                    <Input
                      id="edit-commission-rate"
                      type="number"
                      value={planForm.commission_rate}
                      onChange={(e) => setPlanForm({...planForm, commission_rate: e.target.value})}
                      placeholder="15"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-max-chefs">Nombre maximum de chefs</Label>
                  <select
                    id="edit-max-chefs"
                    className="w-full p-2 border rounded-md"
                    value={planForm.max_chefs}
                    onChange={(e) => setPlanForm({...planForm, max_chefs: e.target.value})}
                  >
                    <option value="5">5 chefs</option>
                    <option value="10">10 chefs</option>
                    <option value="25">25 chefs</option>
                    <option value="50">50 chefs</option>
                    <option value="100">100 chefs</option>
                    <option value="unlimited">Illimité</option>
                  </select>
                </div>

                <div>
                  <Label>Fonctionnalités (une par ligne)</Label>
                  <textarea
                    className="w-full p-2 border rounded-md"
                    rows="4"
                    value={planForm.features.join('\n')}
                    onChange={(e) => setPlanForm({...planForm, features: e.target.value.split('\n').filter(f => f.trim())})}
                    placeholder="Accès aux ateliers&#10;Support prioritaire&#10;Commission réduite"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit-analytics"
                      checked={planForm.analytics}
                      onChange={(e) => setPlanForm({...planForm, analytics: e.target.checked})}
                    />
                    <Label htmlFor="edit-analytics">Analytics inclus</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit-priority-support"
                      checked={planForm.priority_support}
                      onChange={(e) => setPlanForm({...planForm, priority_support: e.target.checked})}
                    />
                    <Label htmlFor="edit-priority-support">Support prioritaire</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit-api-access"
                      checked={planForm.api_access}
                      onChange={(e) => setPlanForm({...planForm, api_access: e.target.checked})}
                    />
                    <Label htmlFor="edit-api-access">Accès API</Label>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-is-active"
                    checked={planForm.is_active}
                    onChange={(e) => setPlanForm({...planForm, is_active: e.target.checked})}
                  />
                  <Label htmlFor="edit-is-active">Plan actif</Label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowEditPlanModal(false);
                    setEditingPlan(null);
                  }}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={() => handleSavePlan(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Sauvegarder les Modifications
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;